// Foundations
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Services
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { PaymentService } from 'src/app/services/payment.service';

// Models
import { PaymentCreditCard } from 'src/app/models/payment/payment-card.model';

// Extensions
// import hmacSHA256 from 'crypto-js/hmac-sha256';
// import Hex from 'crypto-js/enc-hex';
import { get } from 'scriptjs';

// ENV
import { environment } from '../../../../environments/environment';
import { TranSafeIFrameHash } from 'src/app/services/graphql.service';


const iframeId = 'iframe';
const iframeDomain = environment.tranSafe.paymentFrameUrl;

const POSTAL_CODE_REGEX =
  /^(\d{5}(\-\d{4})?|[A-Za-z]\d[A-Za-z][ \-]?\d[A-Za-z]\d)$/;

declare function PaymentFrame(iframeElementId, iframeDomain): any;

@Component({
  selector: 'app-payment-details-form',
  templateUrl: './payment-details-form.component.html',
  styleUrls: ['./payment-details-form.component.scss'],
})
export class PaymentDetailsFormComponent implements OnInit {
  // #region -> Property <-
  // ----------

  @ViewChild('iframe') iframe: ElementRef;
  @Input() isEditMode = false;
  @Input() franchiseId: string;
  @Input() paymentId: string;
  @Output() submittedInSuccess = new EventEmitter<FormGroup>();
  @Output() submittedInFailure = new EventEmitter<string>();

  paymentFrame;
  form: FormGroup;
  paymentCreditCard: PaymentCreditCard;
  isPaymentFrameJsLoaded = false;
  iframeReady = false;

  monthList: string[];
  yearList: string[];

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    private fb: FormBuilder,
    private loadingIndicatorService: LoadingIndicatorService,
    private paymentService: PaymentService
  ) {
    this.monthList = [...Array(12).keys()].map((m) =>
      (++m).toString().padStart(2, '0')
    );

    const thisYear = new Date().getFullYear();
    this.yearList = [...Array(20).keys()].map((y) =>
      (thisYear + y).toString().slice(-2)
    );
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Lifecycle <-
  // ----------

  ngOnInit(): void {
    //
    // Init the form with default values.
    this.form = this.fb.group(
      {
        cardholderName: ['', [Validators.required]],
        // postalCode: ['', [Validators.required]],
        cardNickname: '',
        cardToken: '',
        creditCardNumber: '**** **** **** 8764',
        cardType: '',
        cvv: '***',
        expMonth: '12',
        expYear: '27',
      },
      { validator: this._checkPostalCode }
    );

    if (!this.isEditMode) {
      // In "Add" mode
      // Load the "iFrame"
      this.loadPaymentIframe();
    }
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method <-
  // ----------

  get f() {
    return this.form.controls;
  }

  loadPaymentIframe() {
    this.isPaymentFrameJsLoaded = false;
    this.loadingIndicatorService.show();
      // https://stackoverflow.com/a/53026833/3643497
      get(iframeDomain + '/PaymentFrame/PaymentFrame.js', () => {
        this.isPaymentFrameJsLoaded = true;
        this._initPaymentFrame();
      });
  }

  iframeOnLoad() {
    if (this.isPaymentFrameJsLoaded) {
      this.loadingIndicatorService.hide();
      this.iframeReady = true;
    }
  }

  submit() {
    if (!this.isEditMode) {
      const iframeElement = this.iframe.nativeElement;
      iframeElement.contentWindow.postMessage(
        JSON.stringify({ type: 'submitPaymentData' }),
        iframeDomain
      );
    } else {
      // Update Payment Method
      this.submittedInSuccess.emit(this.form);
    }
  }

  /*
   * patches data passed from payment edit component
   */
  patchData(paymentCreditCard: PaymentCreditCard) {
    this.paymentCreditCard = paymentCreditCard;
    this.form.patchValue({
      cardholderName: paymentCreditCard.cardholderName,
      // postalCode: ['', [Validators.required]],
      cardToken: paymentCreditCard.cardToken,
      cardNickname: paymentCreditCard.cardNickname,
      creditCardNumber: paymentCreditCard.cardAccount,
      cardType: paymentCreditCard.cardType,
      cvv: '***',
      expMonth: paymentCreditCard.expMonth.padStart(2, '0'),
      expYear: paymentCreditCard.expYear,
    });
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  private _checkPostalCode(group: FormGroup) {
    const errors: { [k: string]: any } = {};
    // const postalCode = group.controls.postalCode.value;

    // No need to check on "zipcode" for now.
    // if (!POSTAL_CODE_REGEX.test(postalCode)) {
    //   errors.postalCode = true;
    // }

    return errors;
  }

  /**
   * Convert a "string" into an object which contains all attributes.
   */
  private _transformIFrameAttributes(hash: TranSafeIFrameHash) {
    //
    const attributes: { [attribute: string]: string } = {};

    //
    hash.iFrameAttributes.split(' ').forEach((attributePair) => {
      const parts = attributePair.split('=');
      if (parts.length === 2) {
        attributes[parts[0]] = parts[1].replace(/['"]+/g, '');
      }
    });

    //
    return attributes;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method - TranSafe iFrame <-
  // ----------

  private _initPaymentFrame() {
    if (this.franchiseId) {
      this.paymentService.createTranSafeIFrameHash(this.franchiseId);
    } else {
      this.paymentService.createTranSafeIFrameHash();
    }
    //
    this.paymentService.tranSafeIFrameHash$.subscribe((tranSafeIFrameHash) => {
      //
      if (tranSafeIFrameHash) {
        //
        const iFrameAttributes =
          this._transformIFrameAttributes(tranSafeIFrameHash);
        const iframeElement = this.iframe.nativeElement;
        this._setupIFrame(iframeElement, iFrameAttributes);

        // https://stackoverflow.com/a/48413711/3643497
        this.paymentFrame = new (PaymentFrame as any)(iframeId, iframeDomain);
        this._setupPaymentFrameCallback();
        this.paymentFrame.request();
      } else {
        // Error in load the iframe
        this.loadingIndicatorService.hide();
      }
    });
  }

  private _setupPaymentFrameCallback() {
    this.paymentFrame.setPaymentSubmittedCallback((response) => {
      //
      if (response.code === 'AUTH') {
        //
        this.f.cardToken.setValue(response.ticket);
        this.f.cardType.setValue(response.cardtype);
        //
        this.submittedInSuccess.emit(this.form);
      } else {
        // Send error message back
        this.submittedInFailure.emit(response.verbiage);
      }
    });
  }

  private _setupIFrame(iframeElem, iFrameAttributes) {
    //
    for (const key of Object.keys(iFrameAttributes)) {
      iframeElem.setAttribute(key, iFrameAttributes[key]);
    }

    const iframeName = 'iframe-' + this._uniqId();
    iframeElem.setAttribute('name', iframeName);

    /* Values that will be needed for generating the HMAC */
    // const hostDomain = 'https://' + window.location.host;
    // const monetraUsername = 'test_ecomm:public';
    // const monetraPassword = 'publ1ct3st';
    // const hmacFields: { [key: string]: any } = {};

    /* "timestamp", "domain", "sequence", and "username" are the required HMAC fields. */

    /* Current Unix timestamp */
    // hmacFields.timestamp = this.time();

    /* Domain of the website that will host the iframe */
    // hmacFields.domain = hostDomain;

    /* Merchant-specified alphanumeric value for tracking/verification purposes.
     * In production this should be dynamically generated.
     */
    // hmacFields.sequence = this._uniqId();

    /* Username of the Monetra merchant user that will be used to request the iframe
     * and generate the ticket
     */
    // hmacFields.username = monetraUsername;

    /* Optional field. This is the URL of the CSS file that will be used to style the iframe's contents. */
    // hmacFields['css-url'] = hostDomain + '/assets/css/iframe.css';

    // hmacFields['include-cardholdername'] = 'no';
    // hmacFields['include-street'] = 'no';
    // hmacFields['include-zip'] = 'yes';
    // hmacFields['expdate-format'] = 'separate-selects';

    // hmacFields['auto-reload'] = 'yes';
    // hmacFields.autocomplete = 'no';
    // hmacFields['include-submit-button'] = 'no';

    /* Concatenate all of the defined HMAC fields into a string with no delimiters */
    // $data_to_hash = implode("", $hmac_fields);
    // let dataToHash = '';
    // for (const key of Object.keys(hmacFields)) {
    //   dataToHash += hmacFields[key];
    // }

    /* Generate the HMAC, using the Monetra merchant user's password as the key */
    // $hmac = hash_hmac('sha256', $data_to_hash, $monetra_password);
    // const hash = hmacSHA256(dataToHash, monetraPassword);
    // const hmac = Hex.stringify(hash);

    /* Assemble a string containing the "data-" attributes for the iframe element.
     * This will consist of the HMAC itself and all of the fields included in the HMAC.
     */
    /*
    $iframe_attributes = [
        'data-hmac-hmacsha256="' . $hmac . '"'
    ];
    foreach ($hmac_fields as $key => $value) {
        $iframe_attributes[] = 'data-hmac-' . $key . '="' . $value . '"';
    }
    $iframe_attribute_string = implode(" ", $iframe_attributes);
    */
    // iframeElem.setAttribute('data-hmac-hmacsha256', hmac);
    // for (const key of Object.keys(hmacFields)) {
    //   iframeElem.setAttribute('data-hmac-' + key, hmacFields[key]);
    // }

    // const iframeName = 'iframe-' + this._uniqId();
    // iframeElem.setAttribute('name', iframeName);
  }

  private _uniqId(prefix = '', random = false) {
    const sec = Date.now() * 1000 + Math.random() * 1000;
    const id = sec.toString(16).replace(/\./g, '').padEnd(14, '0');
    return `${prefix}${id}${
      random ? `.${Math.trunc(Math.random() * 100000000)}` : ''
    }`;
  }

  // https://stackoverflow.com/a/48593447/3643497
  private _time() {
    return Math.round(Date.now() / 1000);
  }

  // ----------
  // #endregion

  /////////////////////////////
}
